/**
 * Auth domain enums, types and interfaces shared by backend and frontend,
 * grouped by the Better Auth core or plugin they belong to. Stores and
 * components declare no auth types of their own.
 *
 * No imports: Better Auth shapes are re-declared here, never imported.
 *
 * The reason for this is to prevent deep imports from frontend into backed BA code.
 *
 * Enum values that mirror database columns are written by Better Auth, so
 * each member must equal its exact string literal; the columns carry no
 * constraint. When a plugin config adds values (custom roles, social
 * providers), extend the enum in the same change. Error-code types need the
 * auth client's inferred type and live in `frontend/src/shared_ui/auth_errors.ts`.
 */

// ===========================================================================
// Core (email & password, sessions, accounts)
// ===========================================================================

/**
 * account.providerId values, one per login method. 'credential' is email and
 * password; social provider ids are added when those providers are enabled.
 */
export enum AuthProviderId {
   CREDENTIAL = 'credential',
}

/**
 * The signed-in user's identity as the frontend tracks it (both apps).
 * username/displayUsername are contributed by the username plugin.
 */
export interface IAuthUser {
   id: string;
   name: string;
   email: string;
   emailVerified: boolean;
   username: string | null;
   displayUsername: string | null;
   image: string | null;
   /** System-level role (admin plugin; fresh accounts get 'user'). */
   role: UserRole;
}

/** Error from an auth flow, with the Better Auth code for branching. */
export interface IAuthFlowError {
   /** Better Auth error code; null when absent or not a known code. */
   code: string | null;
   message: string;
}

/** Outcome of consuming an email-verification token. */
export type VerifyEmailOutcome = 'verified' | 'expired' | 'invalid';

/** A signed-in device session, as the sessions panel shows it. */
export interface ISessionInfo {
   id: string;
   /** The session token — the identifier revocation calls use. */
   token: string;
   ipAddress: string | null;
   userAgent: string | null;
   createdAt: string;
   updatedAt: string;
   /** Whether this is the session the current browser is using. */
   isCurrent: boolean;
}

/** Result of loading the caller's sessions. */
export type SessionsLookup =
   | {kind: 'ok'; sessions: ISessionInfo[]}
   | {kind: 'error'; message: string};

// ===========================================================================
// Username plugin
// ===========================================================================

/** Result of an isUsernameAvailable check ('invalid' = rejected by the
 * server's username rules: letters, numbers, dots, underscores). */
export type UsernameAvailability = 'available' | 'taken' | 'invalid';

// ===========================================================================
// Admin plugin (system-level roles, bans, impersonation)
// ===========================================================================

/**
 * System-wide roles on user.role (admin plugin defaults). Distinct from
 * OrgRole, which is per organization. Better Auth allows comma-separated
 * multiple roles; this app assigns one.
 */
export enum UserRole {
   ADMIN = 'admin',
   USER = 'user',
}

/** Narrow a raw user.role value (may be comma-separated) to the enum. */
export function toUserRole(role: string | null | undefined): UserRole {
   const first = (role ?? '').split(',')[0];
   const known = Object.values(UserRole).find((r) => r === first);
   return known ?? UserRole.USER;
}

/** A user row as the admin plugin's listUsers returns it, flattened for
 * the in-app admin surfaces. */
export interface IManagedUser {
   id: string;
   name: string;
   email: string;
   emailVerified: boolean;
   username: string | null;
   role: UserRole;
   banned: boolean;
   banReason: string | null;
   /** ISO timestamp the ban lifts, null = permanent (or not banned). */
   banExpires: string | null;
   createdAt: string;
   /** Last activity written by the Better Auth dash plugin, at most every
    * ~5 minutes; null until the user's first tracked request. */
   lastActiveAt: string | null;
}

// ===========================================================================
// Organization plugin (organizations, members, invitations)
// ===========================================================================

/**
 * Per-organization roles on member.role (organization plugin defaults).
 * Better Auth allows comma-separated multiple roles; this app assigns one.
 * A custom role is added here and in the createAccessControl config together.
 */
export enum OrgRole {
   OWNER = 'owner',
   ADMIN = 'admin',
   MEMBER = 'member',
}

/** Narrow a raw member.role value (may be comma-separated) to the enum; unknown values give MEMBER. */
export function toOrgRole(role: string | null | undefined): OrgRole {
   const first = (role ?? '').split(',')[0];
   const known = Object.values(OrgRole).find((r) => r === first);
   return known ?? OrgRole.MEMBER;
}

/**
 * Invitation lifecycle (organization plugin). Expiry is computed from
 * invitation.expiresAt, not stored as a status.
 */
export enum InvitationStatus {
   PENDING = 'pending',
   ACCEPTED = 'accepted',
   REJECTED = 'rejected',
   CANCELED = 'canceled',
}

/** Display metadata for an organization role. */
export interface IOrgRoleDef {
   role: OrgRole;
   label: string;
   description: string;
}

/** Display metadata for the real organization roles. */
export const ORG_ROLE_DEFS: readonly IOrgRoleDef[] = [
   {role: OrgRole.OWNER, label: 'Owner', description: 'Full control, including deletion'},
   {role: OrgRole.ADMIN, label: 'Admin', description: 'Manage members, invitations, and settings'},
   {role: OrgRole.MEMBER, label: 'Member', description: 'Work in boards; no member management'},
];

/** Result of a checkSlug availability check. */
export type SlugAvailability = 'available' | 'taken' | 'invalid';

/** A pending/expired invitation row for an organization. */
export interface IOrgInvitation {
   id: string;
   email: string;
   role: OrgRole;
   status: InvitationStatus;
   expiresAt: string;
   inviterName: string;
   isExpired: boolean;
}

/** Invitation details for the accept page (recipient view). */
export interface IInvitationDetails {
   id: string;
   email: string;
   role: OrgRole;
   organizationName: string;
   inviterEmail: string;
   expiresAt: string;
}

/** An invitation addressed to the signed-in user (my-invitations view).
 * listUserInvitations returns pending rows only, with the org name joined. */
export interface IMyInvitation {
   id: string;
   organizationId: string;
   organizationName: string;
   role: OrgRole;
   expiresAt: string;
}

/** Outcome of looking up an invitation for the accept page. The server only
 * reveals details to the signed-in session matching the invitee email, so
 * 'wrong-account' is a distinct outcome from 'not-found'. */
export type InvitationLookup =
   | {kind: 'found'; invitation: IInvitationDetails}
   | {kind: 'not-found'}
   | {kind: 'wrong-account'};

// ===========================================================================
// Last-login-method plugin
// ===========================================================================

/**
 * user.lastLoginMethod values (last-login-method plugin). Social provider
 * ids are added when social sign-in is enabled.
 */
export enum LoginMethod {
   EMAIL = 'email',
   USERNAME = 'username',
}
